import React from 'react';
import { 
  Plug, Server, Lock, 
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const IntegrationCard = ({ icon: Icon, title, description, status, disabled }) => (
  <Card className={`border-border ${disabled ? 'bg-card/50 opacity-60' : 'bg-card'}`}>
    <CardContent className="flex items-center gap-4 p-6">
      <div className={`flex h-14 w-14 items-center justify-center rounded-xl ${disabled ? 'bg-muted' : 'bg-primary/10'}`}>
        <Icon className={`h-7 w-7 ${disabled ? 'text-muted-foreground' : 'text-primary'}`} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-foreground">{title}</h3>
          <Badge 
            variant={status === 'Coming Soon' ? 'secondary' : 'default'}
            className="text-xs"
          >
            {status}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </div>
      {!disabled && (
        <ChevronRight className="h-5 w-5 text-muted-foreground" />
      )}
    </CardContent>
  </Card>
);

export function Integrations() {
  return (
    <div className="space-y-6" data-testid="integrations-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
        <p className="text-muted-foreground">Connect external services and streaming infrastructure</p>
      </div>

      {/* Info Card */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="flex items-start gap-4 p-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Plug className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Integration Hub</h3>
            <p className="text-sm text-muted-foreground mt-1">
              This section will allow you to connect and monitor external streaming services.
              New integrations are being developed and will be available soon.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Integrations List */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Streaming Infrastructure</h2>
        
        {/* Nimble Streamer - Coming Soon */}
        <IntegrationCard
          icon={Server}
          title="Nimble Streamer"
          description="Coming soon: Nimble Streamer stats + health monitoring. Real-time stream status, bandwidth usage, and performance metrics."
          status="Coming Soon"
          disabled={true}
        />
      </div>

      {/* Authentication Section */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Authentication & Security</h2>
        
        <IntegrationCard
          icon={Lock}
          title="aMember Integration"
          description="User authentication and subscription management. Currently active and handled by the API."
          status="Active"
          disabled={false}
        />
      </div>

      {/* Roadmap Note */}
      <Card className="border-border bg-card/50">
        <CardHeader>
          <CardTitle className="text-base">Development Roadmap</CardTitle>
          <CardDescription>
            Planned integrations for future releases
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Nimble Streamer real-time stats and health monitoring
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
              Stream quality analytics and alerts
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
              CDN performance metrics
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
              Automated stream failover configuration
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

export default Integrations;
